import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { 
  Info, HelpCircle, Shield, Mail, MessageSquare, 
  ExternalLink, RefreshCw, FileText, Lock, ArrowRight,
  ChevronDown, ChevronUp, Search, Share2, Copy, Check, Smartphone
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const FAQ_ITEMS_GENERAL = [
  {
    q: 'What is LexAgenda?',
    a: 'LexAgenda is a comprehensive digital platform for Philippine corporate governance. It helps manage board meetings, resolutions, minutes, compliance, and governance workflows in accordance with the Revised Corporation Code (RA 11232) and SEC regulations.',
  },
  {
    q: 'What is the Stakeholders Portal?',
    a: 'The Stakeholders Portal is where board members and stakeholders can view shared documents, announcements, polls, meeting summaries, and governance requests. Access it via the "Stakeholders" tab in the bottom navigation bar.',
  },
  {
    q: 'What is the Library?',
    a: 'The Library is your centralized document repository organized into categories: Corporate Books, Meeting Docs, Governance, Regulatory Docs, Communications, and Other Resources. You can upload and manage documents with classification levels (Open, Internal, Classified).',
  },
  {
    q: 'How do I navigate the app?',
    a: 'The app has two navigation bars. The top bar provides quick access to core Secretariat tools (Governance Hub, Meetings, Tasks, Meeting Records). The bottom bar gives access to general sections (Home, Library, Stakeholders, Helpdesk, Subscription).',
  },
];

const FAQ_ITEMS_SECRETARIAT = [
  {
    q: 'How do I schedule a board meeting?',
    a: 'Navigate to "Meetings" from the top menu, then click "+ New Meeting". Fill in the meeting details such as title, date, time, venue, and body (e.g., Board of Directors). You can also set the meeting type and basis.',
  },
  {
    q: 'How do I send meeting notices?',
    a: 'Go to the meeting detail page and click "Send Notices". You can select recipients, compose the notice content, and if your SMTP is configured in Secretariat Settings > Email, notices will be automatically sent via email.',
  },
  {
    q: 'How do I upload documents to the Library?',
    a: 'Go to the Library page, select the appropriate tab (e.g., Corporate Books, Meeting Docs, Governance), and click the "Upload" button. Fill in the title, description, classification level, and attach your file.',
  },
  {
    q: 'How do I share meeting summaries with stakeholders?',
    a: 'Open a meeting that has an AI-generated "Summary of Discussions". Toggle the "Share to Portal" switch to make the summary visible on the Stakeholders Portal for board members to review.',
  },
  {
    q: 'What is the Governance Hub?',
    a: 'The Governance Hub allows the Secretariat to share documents for review and approval, conduct polls, manage announcements, and handle stakeholder requests. It supports deadline tracking, comment threads, and document status workflows.',
  },
  {
    q: 'How do I configure email (SMTP)?',
    a: 'Go to Secretariat Settings > Email tab. Enter your SMTP server details (host, port, email, password). You can use Gmail, Outlook, or any custom SMTP server. Click "Save" then "Send Test Email" to verify.',
  },
  {
    q: 'How do I add an electronic signature?',
    a: 'Go to Secretariat Settings > E-Signature tab. You can either draw your signature using the signature pad or upload an image of your signature (PNG or JPG).',
  },
  {
    q: 'How do I manage shareholders?',
    a: 'Shareholders are managed under Secretariat Settings > Users & Roles > Shareholders sub-tab. You can add, edit, and remove shareholder records including their share count and class.',
  },
  {
    q: 'Can I use AI to draft documents?',
    a: 'Yes! If AI is enabled in your settings (Secretariat Settings > AI Settings), you can use AI to draft meeting minutes, resolutions, and certificates. Look for the "Draft with AI" button on relevant pages.',
  },
  {
    q: 'How do I track board composition?',
    a: 'Go to Secretariat Settings > Board tab. You can create snapshots of your board composition with member names, positions, types, and term dates. Track changes over time with the history view.',
  },
];

const TERMS_CONTENT = `
Terms and Conditions of Use

Last Updated: March 2026

1. Acceptance of Terms
By accessing and using LexAgenda, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use this platform.

2. Description of Service
LexAgenda is a corporate governance management platform designed for Philippine corporate secretaries. It provides tools for managing board meetings, resolutions, minutes, compliance, templates, and governance workflows.

3. User Accounts
- You must provide accurate and complete registration information
- You are responsible for maintaining the confidentiality of your account credentials
- You must notify us immediately of any unauthorized use of your account
- Each user account is for a single user and may not be shared

4. Data Ownership
- All data you enter into LexAgenda remains your property
- We do not claim ownership over your corporate records, documents, or information
- We maintain backups and security measures to protect your data

5. Acceptable Use
You agree not to:
- Use the platform for any illegal purpose
- Upload malicious code or attempt to breach security
- Share access credentials with unauthorized parties
- Use the platform to send unsolicited communications

6. Service Availability
We strive for 99.9% uptime but do not guarantee uninterrupted service. Scheduled maintenance will be communicated in advance.

7. Limitation of Liability
LexAgenda is a governance tool and does not replace legal advice. The Corporate Secretary retains statutory responsibilities under RA 11232 (Revised Corporation Code) and SEC regulations.

8. Modifications
We reserve the right to modify these terms at any time. Continued use of the platform constitutes acceptance of modified terms.

9. Governing Law
These terms are governed by the laws of the Republic of the Philippines.
`;

const PRIVACY_CONTENT = `
Privacy Policy

Last Updated: March 2026

1. Information We Collect
- Account information: name, email, organization details
- Usage data: pages visited, features used, timestamps
- Corporate data: meetings, resolutions, minutes, and other governance documents you create

2. How We Use Your Information
- To provide and maintain the LexAgenda platform
- To authenticate users and manage access
- To improve our services and user experience
- To communicate important updates and notices

3. Data Storage and Security
- All data is stored in secure, encrypted databases
- We use industry-standard security measures including encryption in transit and at rest
- Access to user data is restricted to authorized personnel only
- Regular security audits are conducted

4. Data Sharing
We do not sell, trade, or share your personal or corporate data with third parties, except:
- When required by law or legal process
- With your explicit consent
- With service providers who assist in platform operations (under strict confidentiality agreements)

5. Data Retention
- Active account data is retained for the duration of your subscription
- Upon account deletion, your data will be permanently removed within 30 days
- Backup copies may be retained for up to 90 days

6. Your Rights
Under the Philippine Data Privacy Act (RA 10173), you have the right to:
- Access your personal data
- Correct inaccurate data
- Request deletion of your data
- Object to data processing
- Data portability

7. Cookies
We use essential cookies for authentication and session management. No third-party tracking cookies are used.

8. Contact
For privacy-related inquiries: support@lexdoright.com
Subject: "Privacy Inquiry - LexAgenda"
`;

const REFUND_CONTENT = `
Refund Policy

Last Updated: March 2026

1. Subscription Refunds
- Refund requests must be submitted within 14 days of payment
- Refunds are processed within 5-7 business days
- Partial month refunds are calculated on a pro-rata basis

2. Eligibility
Refunds may be granted for:
- Service outages exceeding 24 consecutive hours
- Features not functioning as described
- Billing errors or duplicate charges
- Account cancellation within the refund period

3. Non-Refundable Items
The following are non-refundable:
- Setup or onboarding fees (if applicable)
- Custom development or integration work
- Services already rendered for the billing period

4. How to Request a Refund
Contact our support team:
Email: support@lexdoright.com
Subject: "Refund Request - LexAgenda"

Please include:
- Your account email
- Payment reference number
- Reason for the refund request

5. Processing
- All refund requests are reviewed within 2 business days
- Approved refunds are processed to the original payment method
- You will receive email confirmation once the refund is processed
`;

const HelpSupport = () => {
  const { user } = useAuth();
  const isSecretariat = ['corporate_secretary', 'secretariat_staff', 'super_admin', 'landlord'].includes(user?.role);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [policyDialog, setPolicyDialog] = useState(null);
  const [copied, setCopied] = useState(false);

  const allFaqItems = [
    ...FAQ_ITEMS_GENERAL,
    ...(isSecretariat ? FAQ_ITEMS_SECRETARIAT : []),
  ];

  const filteredFaq = allFaqItems.filter(
    item => !searchQuery ||
      item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.a.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const shareMessage = `Check out LexAgenda - Your complete digital platform for Philippine corporate governance (RA 11232, SEC Compliance & Board Management)!\n${window.location.origin}`;

  const handleCopyShare = () => {
    navigator.clipboard.writeText(shareMessage).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="help-page">
      <h1 className="text-2xl font-bold text-[#1A1D21]">Helpdesk</h1>

      {/* About Us */}
      <Card className="border-[#E2E6EA]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg shrink-0">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-[#1A1D21] mb-2">About Us</h2>
              <p className="text-sm text-[#5A6570] leading-relaxed">
                <strong>LexAgenda</strong> is a proprietary corporate governance application owned by <strong>SRAGCircle OPC</strong>, 
                developed under the brand <strong>LexDoRight</strong>. It is designed as a comprehensive digital platform for 
                Philippine corporate secretaries, helping manage board meetings, resolutions, minutes, compliance, 
                and governance workflows in accordance with the Revised Corporation Code (RA 11232).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* App Details */}
      <Card className="border-[#E2E6EA]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 rounded-lg shrink-0">
              <Smartphone className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-[#1A1D21] mb-3">App Details</h2>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5A6570]">App Name:</span>
                  <span className="text-sm font-medium text-[#1A1D21]">LexAgenda</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5A6570]">Version:</span>
                  <span className="text-sm font-medium text-[#1A1D21]">1.0.0</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5A6570]">Last Updated:</span>
                  <span className="text-sm font-medium text-[#1A1D21]">March 2026</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Share App */}
      <Card className="border-[#E2E6EA]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 rounded-lg shrink-0">
              <Share2 className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-[#1A1D21] mb-3">Share App</h2>
              <div className="bg-[#F0F2F4] rounded-lg p-3 mb-3">
                <p className="text-sm text-[#5A6570] whitespace-pre-line" data-testid="share-message-text">
                  {shareMessage}
                </p>
              </div>
              <Button
                onClick={handleCopyShare}
                className={`w-full ${copied ? 'bg-green-600 hover:bg-green-700' : 'bg-[#00A3A3] hover:bg-[#008585]'}`}
                data-testid="copy-share-btn"
              >
                {copied ? (
                  <><Check className="h-4 w-4 mr-2" />Copied!</>
                ) : (
                  <><Copy className="h-4 w-4 mr-2" />Copy to Share</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQ Section - Available to all users */}
      <Card className="border-[#E2E6EA]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#00A3A3]/10 rounded-lg shrink-0">
              <HelpCircle className="h-5 w-5 text-[#00A3A3]" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-[#1A1D21] mb-3">Frequently Asked Questions</h2>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8B1B9]" />
                <Input
                  placeholder="Search FAQ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 border-[#E2E6EA]"
                  data-testid="faq-search"
                />
              </div>
              <div className="space-y-1">
                {filteredFaq.map((item, i) => (
                  <div key={i} className="border border-[#E2E6EA] rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F0F2F4] transition-colors"
                      onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                      data-testid={`faq-item-${i}`}
                    >
                      <span className="text-sm font-medium text-[#1A1D21] pr-4">{item.q}</span>
                      {expandedFaq === i ? (
                        <ChevronUp className="h-4 w-4 text-[#5A6570] shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-[#5A6570] shrink-0" />
                      )}
                    </button>
                    {expandedFaq === i && (
                      <div className="px-4 pb-3 text-sm text-[#5A6570] leading-relaxed border-t border-[#E2E6EA] pt-3 bg-[#FAFBFC]">
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
                {filteredFaq.length === 0 && (
                  <p className="text-sm text-[#A8B1B9] text-center py-4">No matching questions found</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Onboarding Support - Secretariat only */}
      {isSecretariat && (
      <Card className="border-[#E2E6EA]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#00A3A3]/10 rounded-lg shrink-0">
              <HelpCircle className="h-5 w-5 text-[#00A3A3]" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-[#1A1D21] mb-2">Onboarding Support</h2>
              <p className="text-sm text-[#5A6570] mb-3">
                Need help getting started? Review the app walkthrough from the Helpdesk to learn about key features.
              </p>
              <button 
                className="flex items-center gap-2 text-sm font-medium text-[#00A3A3] hover:text-[#008585] transition-colors" 
                data-testid="retake-tutorial-btn"
                onClick={() => {
                  localStorage.removeItem('lexagenda_onboarding_done');
                  window.location.reload();
                }}
              >
                <RefreshCw className="h-4 w-4" />
                Retake Tutorial
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Legal & Policies */}
      <Card className="border-[#E2E6EA]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#00A3A3]/10 rounded-lg shrink-0">
              <Shield className="h-5 w-5 text-[#00A3A3]" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-[#1A1D21] mb-3">Legal & Policies</h2>
              <div className="space-y-2.5">
                <button
                  onClick={() => setPolicyDialog('terms')}
                  className="w-full flex items-center gap-2 text-sm text-[#00A3A3] hover:text-[#008585] font-medium transition-colors"
                  data-testid="terms-link"
                >
                  <FileText className="h-4 w-4" />
                  Terms & Conditions
                  <ArrowRight className="h-3.5 w-3.5 ml-auto" />
                </button>
                <button
                  onClick={() => setPolicyDialog('privacy')}
                  className="w-full flex items-center gap-2 text-sm text-[#00A3A3] hover:text-[#008585] font-medium transition-colors"
                  data-testid="privacy-link"
                >
                  <Lock className="h-4 w-4" />
                  Privacy Policy
                  <ArrowRight className="h-3.5 w-3.5 ml-auto" />
                </button>
                <button
                  onClick={() => setPolicyDialog('refund')}
                  className="w-full flex items-center gap-2 text-sm text-[#00A3A3] hover:text-[#008585] font-medium transition-colors"
                  data-testid="refund-link"
                >
                  <Shield className="h-4 w-4" />
                  Refund Policy
                  <ArrowRight className="h-3.5 w-3.5 ml-auto" />
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Us */}
      <Card className="border-[#E2E6EA]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#00A3A3]/10 rounded-lg shrink-0">
              <Mail className="h-5 w-5 text-[#00A3A3]" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-[#1A1D21] mb-3">Contact Us</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-[#1A1D21]">General Inquiries</p>
                  <a href="mailto:support@lexdoright.com?subject=Query/Comments about LexAgenda" className="text-sm text-[#00A3A3] hover:underline">
                    support@lexdoright.com
                  </a>
                  <p className="text-xs text-[#A8B1B9] mt-0.5">Subject: "Query/Comments about LexAgenda"</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A1D21]">Technical Support</p>
                  <a href="mailto:support@lexdoright.com?subject=Request for LexAgenda Support" className="text-sm text-[#00A3A3] hover:underline">
                    support@lexdoright.com
                  </a>
                  <p className="text-xs text-[#A8B1B9] mt-0.5">Subject: "Request for LexAgenda Support"</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback & Suggestions */}
      <Card className="border-[#E2E6EA]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#00A3A3]/10 rounded-lg shrink-0">
              <MessageSquare className="h-5 w-5 text-[#00A3A3]" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-[#1A1D21] mb-2">Feedback & Suggestions</h2>
              <p className="text-sm text-[#5A6570] mb-3">
                We value your input! Help us improve LexAgenda by sharing your feedback.
              </p>
              <ul className="space-y-1.5 mb-4">
                <li className="flex items-center gap-2 text-sm text-[#5A6570]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00A3A3] shrink-0" />
                  Share your ideas for new features
                </li>
                <li className="flex items-center gap-2 text-sm text-[#5A6570]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00A3A3] shrink-0" />
                  Report issues and suggest improvements
                </li>
                <li className="flex items-center gap-2 text-sm text-[#5A6570]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00A3A3] shrink-0" />
                  Connect with other corporate governance professionals
                </li>
              </ul>
              <a href="https://lexdoright.com" target="_blank" rel="noreferrer">
                <Button className="w-full bg-[#00A3A3] hover:bg-[#008585]" data-testid="visit-lexdoright-btn">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Visit LexDoRight Website
                </Button>
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Policy Dialogs */}
      <Dialog open={policyDialog === 'terms'} onOpenChange={(open) => !open && setPolicyDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#00A3A3]" />
              Terms & Conditions
            </DialogTitle>
            <DialogDescription>LexAgenda platform terms and conditions of use</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-[#5A6570] whitespace-pre-line leading-relaxed" data-testid="terms-content">
            {TERMS_CONTENT}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={policyDialog === 'privacy'} onOpenChange={(open) => !open && setPolicyDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-[#00A3A3]" />
              Privacy Policy
            </DialogTitle>
            <DialogDescription>How we collect, use, and protect your data</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-[#5A6570] whitespace-pre-line leading-relaxed" data-testid="privacy-content">
            {PRIVACY_CONTENT}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={policyDialog === 'refund'} onOpenChange={(open) => !open && setPolicyDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#00A3A3]" />
              Refund Policy
            </DialogTitle>
            <DialogDescription>Subscription refund eligibility and process</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-[#5A6570] whitespace-pre-line leading-relaxed" data-testid="refund-content">
            {REFUND_CONTENT}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpSupport;
